
import { AxiosError } from 'axios';
import { Component, Vue, Watch } from 'vue-property-decorator';
import { DataOptions, DataTableHeader } from "vuetify";
import { ActionLogRepositoryInterface, SearchActionLogsOptions, SearchActionLogsResponse } from '@/services/Action-Logs/interfaces/index';
import PageHeader from '@/components/layout/PageHeader.vue';
import {
  LIST_FOOTER_PROPS,
  ListPaginationOptions,
  buildListPaginationQuery,
  defaultListOptions,
  effectivePageSize,
  listPaginationQueryEquals,
  parseListQuery,
} from '@/utils/listPagination';

@Component({
  inject: [
    'actionLogRepository',
  ],
  components: {
    PageHeader,
  },
})
export default class AllActionLogs extends Vue {
  public actionLogRepository!: ActionLogRepositoryInterface;

  public loading: boolean = false;

  public LIST_FOOTER_PROPS = LIST_FOOTER_PROPS;

  public tableHeaders: DataTableHeader[] = [
    { text: "ID", value: "id", sortable: false },
    { text: "Service Name", value: "service_id", sortable: false },
    { text: "Action", value: "action", sortable: false },
    { text: "Agent", value: "agent", sortable: false },
    { text: "Result", value: "result", sortable: false },
    { text: "Message", value: "message", sortable: false },
    { text: "Change", value: "change", sortable: false },
    { text: "Timestamp", value: "timestamp", sortable: false },
  ] as const;

  public data: SearchActionLogsResponse = {
    logs: [],
    metadata: {}
  };

  public dataOptions: DataOptions = {
    ...defaultListOptions(),
    sortBy: [],
    sortDesc: [],
    groupBy: [],
    groupDesc: [],
    multiSort: false,
    mustSort: false,
  };

  private ignoreRouteSync = false;
  private applyingRouteQuery = false;

  public resultItems: string[] = ['info', 'success', 'error'];

  public options: SearchActionLogsOptions = {
    service_ids: [],
    actions: [],
    agents: [],
    results: [],
  };

  public pending: {
    service_ids: string | null;
    actions: string | null;
    agents: string | null;
  } = {
    service_ids: null,
    actions: null,
    agents: null,
  };

  public applyRouteQuery(): void {
    this.applyingRouteQuery = true;
    try {
      const parsed = parseListQuery(this.$route.query);
      this.dataOptions.page = parsed.page;
      this.dataOptions.itemsPerPage = parsed.itemsPerPage;
      this.$nextTick(() => {
        this.applyingRouteQuery = false;
      });
    } catch (err) {
      this.applyingRouteQuery = false;
      throw err;
    }
  }

  public paginationOptions(): ListPaginationOptions {
    return {
      page: this.dataOptions.page,
      itemsPerPage: this.dataOptions.itemsPerPage,
    };
  }

  public buildListQuery(): { [key: string]: string } {
    return buildListPaginationQuery(this.paginationOptions());
  }

  public listQueryEquals(query: { [key: string]: string | (string | null)[] | null | undefined }): boolean {
    return listPaginationQueryEquals(query, this.paginationOptions());
  }

  public async syncRouteQuery(): Promise<void> {
    if (this.ignoreRouteSync || this.applyingRouteQuery) {
      return;
    }

    const query = this.buildListQuery();
    if (this.listQueryEquals(this.$route.query)) {
      return;
    }

    this.ignoreRouteSync = true;
    try {
      await this.$router.replace({ name: 'action_logs', query });
    } catch (err) {
      // Ignore NavigationDuplicated.
    } finally {
      this.$nextTick(() => {
        this.ignoreRouteSync = false;
      });
    }
  }

  public async onPageChange(): Promise<void> {
    await this.syncRouteQuery();
    await this.search();
  }

  public async onItemsPerPageChange(): Promise<void> {
    this.dataOptions.page = 1;
    await this.syncRouteQuery();
    await this.search();
  }

  @Watch('$route')
  public async onRouteChange(): Promise<void> {
    if (this.ignoreRouteSync) {
      return;
    }
    if (this.$route.name !== 'action_logs') {
      return;
    }
    if (this.listQueryEquals(this.$route.query)) {
      return;
    }

    this.applyRouteQuery();
    await this.search();
  }

  public async onFilterSearch(): Promise<void> {
    this.commitAllPending();
    this.dataOptions.page = 1;
    await this.syncRouteQuery();
    await this.search();
  }

  public async search(): Promise<void> {
    this.loading = true;

    try {
      this.data = await this.actionLogRepository.search({
        ...this.options,
        ...dataOptionsToSearchOptions(this.dataOptions),
      });
    } catch (error) {
      this.handleError(error);
    } finally {
      this.loading = false;
    }
  }

  public async created(): Promise<void> {
    this.applyRouteQuery();
    await this.search();
  }

  public async initialize(): Promise<void> {
    await this.search();
  }

  private commitPending(key: 'service_ids' | 'actions' | 'agents'): void {
    const value = (this.pending[key] || '').trim();
    if (!value) {
      this.pending[key] = null;
      return;
    }

    const current = this.options[key] || [];
    if (!current.includes(value)) {
      this.$set(this.options, key, [...current, value]);
    }
    this.pending[key] = null;
  }

  private commitAllPending(): void {
    this.commitPending('service_ids');
    this.commitPending('actions');
    this.commitPending('agents');
  }

  private handleError(error: unknown): void {
    const defaultErrorMessage = 'An error occurred' as const;

    if (isAxiosError(error)) {
      const data = error.response?.data as { message?: string; error?: string | { [key: string]: string } } | undefined;
      let message = data?.message || defaultErrorMessage;
      if (data?.error) {
        message = typeof data.error === 'string'
          ? data.error
          : Object.entries(data.error).map(([field, msg]) => `${field}: ${msg}`).join('; ');
      }
      this.$errorDialog.show(message);
    } else if (error instanceof Error) {
      this.$errorDialog.show(error.message);
    } else {
      this.$errorDialog.show(defaultErrorMessage);
    }
  }
}

function dataOptionsToSearchOptions(dataOptions: DataOptions): SearchActionLogsOptions {
  return {
    page: dataOptions.page,
    page_size: effectivePageSize(dataOptions.itemsPerPage),
  };
}

function isAxiosError(error: unknown): error is AxiosError {
  return (error as AxiosError).isAxiosError !== undefined;
}
